import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from "../../components/Footer";
import DropdownMenu from "../../components/DropdownMenu";
import SEO from "../../context/SEO";
import FAQ from "../../components/FAQ";
import { ShopContext } from "../../context/ShopContext";

// Assets
import alentejoVideo from "../../assets/videos/corkHero.mp4";
import corkTree from "../../assets/videos/corkTree.mp4";
import CorkTreeRaw from "../../assets/cork/corkTree.avif";
import corkTreeGreen from "../../assets/cork/corkTreeGreen.avif";
import carbonSequestering from "../../assets/cork/carbonSequestering.svg";
import corkHarvestingProcessB from "../../assets/videos/corkHarvestingProcessB.mp4";
import CorkManufacturing from "../../assets/videos/CorkManufacturing.mp4";
import styles from "./style.module.scss";

gsap.registerPlugin(ScrollTrigger);

function Cork() {
  const [showElements, setShowElements] = useState("fade-in-down");
  const [opacity, setOpacity] = useState(1);
  const [visible, setVisible] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(0);
  const [vh, setVh] = useState(window.innerHeight * 0.01);

  const prevScrollPos = useRef(window.scrollY);
  const scrollInitiated = useRef(false);

  const { products } = useContext(ShopContext);

  // Update vh on resize
  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight * 0.01);
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  // GSAP Number Counter (only runs once)
  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: `.${styles.scrollingNumberContainer}`,
        start: "top 80%",
        onEnter: () => {
          gsap.to(
            { val: 0 },
            {
              val: 73,
              duration: 3,
              ease: "power2.out",
              snap: { val: 1 },
              onUpdate: function () {
                setCurrentNumber(Math.round(this.targets()[0].val));
              },
            }
          );
        },
      });
    });
    return () => ctx.revert();
  }, []);

  // SINGLE SCROLL HANDLER — This is the only one!
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollingUp = currentScrollPos < prevScrollPos.current;
      const scrollingDown = currentScrollPos > prevScrollPos.current;
      const atTop = currentScrollPos < 10 * vh;

      // First visit: show logo → fade out after 6s
      if (!scrollInitiated.current) {
        setTimeout(() => setVisible(true), 2000);
        setTimeout(() => setVisible(false), 6000);
        scrollInitiated.current = true;
      }

      // Dropdown menu behavior
      if (atTop) {
        setShowElements("full-opacity");
      } else if (scrollingUp) {
        setShowElements("fade-in-up"); // Shows when scrolling up
      } else if (scrollingDown) {
        setShowElements("fade-out-up"); // Hides when scrolling down
      }

      // Background video fade
      const startFadeOut = 20 * vh;
      const endFadeOut = 70 * vh;
      let newOpacity = 1;
      if (currentScrollPos >= startFadeOut && currentScrollPos <= endFadeOut) {
        newOpacity =
          1 - (currentScrollPos - startFadeOut) / (endFadeOut - startFadeOut);
      } else if (currentScrollPos > endFadeOut) {
        newOpacity = 0;
      }
      setOpacity(newOpacity);

      // Update ref
      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [vh]); // Re-run only if vh changes

  // Filter products
  const PenTray = products.filter((p) => p.name.includes("Moonchair"));
  const PlaceMat = products.filter((p) => p.name.includes("Moontable"));
  return (
    <div>
      <DropdownMenu
        overideVisible={visible}
        showElements={showElements}
        iconColour="white"
      />
      <SEO title="Cork Matters" />
      {/* <div style={{ opacity: `${opacity}` }}>
        <ImageCarousel firebasePath={"media/cork/first"} transition="fade" />
      </div> */}
      <div className={`${styles.videoContainer} ${styles.grid}`}>
        <div style={{ opacity: `${opacity}`, gridColumn: "span 12" }}>
          <video
            className={styles.fixedVideo}
            src={alentejoVideo}
            autoPlay
            loop
            muted
            playsInline
            transition="fade"
          ></video>
        </div>
      </div>
      <div className={styles.corkSection}>
        <div className={styles.grid}>
          <div className={`${styles.squarePhoto} ${styles.span6}`}>
            <video src={corkTree} autoPlay muted playsInline></video>
          </div>
          <div className={`${styles.textSection} ${styles.darkGreen}`}>
            <div className={styles.title}>Where the Journey of Cork starts</div>
            <div className={styles.bottomContent}>
              <div className={styles.text4}>
                In the plains of Alentejo, Portugal, the journey of cork begins
                among cork oak groves that spread across the rolling landscape.
                Under a Mediterranean climate marked by hot, dry summers and
                mild, wet winters, these Quercus suber trees grow in thin, sandy
                soils. It takes 39 years for a cork oak to reach maturity for
                its first bark harvest, a careful process that strips the outer
                layer to reveal the trunk's inner red surface, allowing it to
                regenerate. These trees, which can live for over 200 years,
                produce their lightweight, water-resistant bark every nine years
                after the initial harvest, supporting local biodiversity and a
                sustainable resource. From Alentejo's steady conditions comes
                cork's enduring value: a natural material shaped by time and the
                land.
              </div>
            </div>
          </div>
          {/* <div className={`${styles.landscapePhoto} ${styles.span12}`}>
            <video src={alentejoVideo} autoPlay loop muted playsInline />
          </div> */}
        </div>
      </div>
      <div className={styles.corkSection}>
        <div className={styles.grid}>
          <div className={styles.textSection}>
            <div className={styles.title}>Cork Oak</div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                The cork oak is an evergreen tree of the Fagaceae family
                (Quercus suber), to which the chestnut and oak trees also
                belong. There are 465 species of Quercus, mainly in temperate
                and subtropical regions of the Northern Hemisphere. Cork is
                extracted from the species Quercus suber.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the collection
                </Link>
              </div>
            </div>
          </div>
          <div className={styles.image}>
            <img src={CorkTreeRaw} alt="Cork tree freshly cut" />
          </div>
        </div>
      </div>
      {/* <ImageCarousel firebasePath={"media/cork/second"} transition="fade" /> */}

      <div className={styles.corkSection}>
        <div className={styles.grid}>
          <div className={styles.imageLeft}>
            <img src={corkTreeGreen} alt="Cork tree freshly cut" />
          </div>
          <div className={styles.textSectionRight}>
            <div className={styles.topContent}>
              <div className={styles.title}>The Generosity of the Cork Oak</div>
            </div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                In addition to being a very hardy species with extraordinary
                resilience, the cork oak gives much more than it takes. As
                Professor Joaquim Vieira Natividade wrote in 1950: "In the often
                ungrateful soil and climate conditions of our country, the cork
                oak is a precious tree... No tree gives more while demanding so
                little."
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the collection
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <ImageCarousel firebasePath={"media/cork/third"} transition="fade" /> */}
      <div className={styles.corkSection}>
        <div className={styles.grid}>
          <div className={styles.textSection}>
            <div className={styles.topContent}>
              <div className={styles.title}>
                Cork storing and Sequestering Carbon
              </div>
            </div>
            <div className={`${styles.imageSpan6}`}>
              <div className={`${styles.scrollingNumberContainer}`}>
                <div
                  className={styles.scrollingNumber}
                >{`-${currentNumber}`}</div>
                <img src={carbonSequestering} />
              </div>
            </div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                Through the natural growth of cork, CO2 from the atmosphere is
                absorbed and stored within the carbon. Every cork product is
                helping to store Co2 that would otherwise be in the atmosphere
                and causing green house heating effects.
              </div>
            </div>
          </div>
          <div className={`${styles.squarePhoto} ${styles.span6}`}>
            <video
              src={corkHarvestingProcessB}
              autoPlay
              loop
              muted
              playsInline
            />
          </div>
        </div>
      </div>
      {/* <ImageCarousel firebasePath={"media/cork/fourth"} transition="fade" /> */}

      <div className={styles.corkSection}>
        <div className={styles.grid}>
          <div className={`${styles.squarePhoto} ${styles.span6}`}>
            <video
              src={CorkManufacturing}
              autoPlay
              loop
              muted
              playsInline
            ></video>
          </div>
          <div
            className={`${styles.textSectionRight} ${styles.noMobileReorder}`}
          >
            <div className={styles.title}>
              Cork sculpted into Complex 3D Shapes
            </div>
            <div className={styles.bottomContent}>
              <div className={styles.mainText}>
                In the heart of a Portuguese cork factory, raw granules—recycled
                from harvested bark, are transformed into versatile agglomerate
                blocks through a precise, rhythmic process. Vibration tables
                shake out the fine particles into molds, ensuring even
                distribution, before applying heat and pressure to adhere them
                together, creating dense, folded slabs that emerge resilient and
                uniform. These blocks travel along conveyors, ready for both
                CNC-machining and skilled hand labour to sculpt them into
                intricate 3D forms often with a human touch. From humble
                granules to elegant, sustainable designs, this fusion of
                tradition and technique breathes renewed life into natures gift;
                cork.
              </div>
              <div className={styles.linkContainer}>
                <Link
                  to="/products/all"
                  className={`${styles.isAlt} ${styles.underlineLink}`}
                >
                  See the collection
                </Link>
              </div>
            </div>
          </div>
          <div className={styles.imageLeft}>
            {PenTray.map((product) => (
              <div className={styles.imageContainer}>
                <Link
                  to={`/product/${product.id}`}
                  className={styles.productLinkHome}
                >
                  <img
                    src={product.variations[0].imageSrc[0]}
                    alt="Cork Trees"
                  />
                  <p className={styles.productTitleHome}>
                    {product.family
                      ? product.family.toUpperCase()
                      : product.name.toUpperCase()}{" "}
                    {product.family ? product.size.toUpperCase() : ""}{" "}
                  </p>
                </Link>
              </div>
            ))}
          </div>
          <div className={styles.imageRight}>
            {PlaceMat.map((product) => (
              <div className={styles.imageContainer}>
                <Link
                  to={`/product/${product.id}`}
                  className={styles.productLinkHome}
                >
                  <img
                    src={product.variations[0].imageSrc[0]}
                    alt="Cork Trees"
                  />
                  <p className={styles.productTitleHome}>
                    {product.family
                      ? product.family.toUpperCase()
                      : product.name.toUpperCase()}{" "}
                    {product.family ? product.size.toUpperCase() : ""}{" "}
                  </p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FAQ />
      <Footer />
    </div>
  );
}

export default Cork;
